import React, { useMemo } from "react";
import ConditionalRender from "../../../ConditionalRender";
import { Loader } from "../Loader.jsx";

/**
 * ProgressBar — Horizontal progress indicator with optional label and spinner.
 *
 * Used inside FileUploader during uploads, or standalone for any async operation.
 * The fill width is set via inline style as a percentage.
 *
 * @param {number}  progress   - 0–100 percentage
 * @param {string}  label      - Text description (default: "Uploading")
 * @param {string}  fileName   - Optional file name shown next to the label
 * @param {boolean} showInfo   - Show the info bar (label + loader) below the bar
 * @param {boolean} showLoader - Show a spinner next to the label
 * @param {string}  size       - 'Small' | 'Medium' | 'Large' — controls bar height
 * @param {string}  variant    - Color variant (maps to CSS class)
 */
export const ProgressBar = ({
    progress = 0,
    label = "Uploading",
    fileName = "",
    showInfo = true,
    showLoader = true,
    size = "Medium",
    variant = "primary",
    customClass = "",
    customStyle = {},
    barStyle = {},
    fillStyle = {},
    labelStyle = {},
    fileNameStyle = {},
}) => {
    const computedContainerClass = useMemo(
        () => `progress-bar-container progress-bar--${size.toLowerCase()} ${customClass}`,
        [size, customClass]
    );
    const computedBarClass = useMemo(
        () => `progress-bar-background progress-bar-variant--${variant}`,
        [variant]
    );
    const computedFillClass = useMemo(
        () => `progress-bar-fill progress-bar-variant--${variant}`,
        [variant]
    );

    return (
        <div className={computedContainerClass} style={customStyle}>
            {/* The bar track + fill */}
            <div className={computedBarClass} style={barStyle}>
                <div
                    className={computedFillClass}
                    style={{ width: `${progress}%`, ...fillStyle }}
                />
            </div>

            {/* Info row below the bar: spinner + label + file name */}
            <ConditionalRender if={showInfo}>
                <div className="progress-bar-info">
                    {showLoader && (
                        <Loader
                            size="S"
                            type={variant}
                            isLoading={true}
                            className="progress-bar-loader"
                        />
                    )}
                    <span className="progress-bar-label body-s" style={labelStyle}>
                        {label}{" "}
                        {fileName && (
                            <span className="progress-bar-filename body-s-bold" style={fileNameStyle}>
                                {fileName}
                            </span>
                        )}
                    </span>
                </div>
            </ConditionalRender>
        </div>
    );
};

export default ProgressBar;
